<script setup>
import {computed, inject, reactive, ref, toRaw, watch} from "vue";
//import functions
import {numeric, required} from "@vuelidate/validators";
import {showMessage} from "@/utils/showMessage.js";
//import components
import {InputText, FloatLabel, Dialog, Listbox, Checkbox} from "primevue";
import {Icon} from "@iconify/vue";
//import stores
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useMarketMapStore} from "@/stores/marketMap.js";
import Select from "primevue/select";
import {useConfigData} from "@/composables/useConfigData.js";
import {useVuelidate} from "@vuelidate/core";

//stores
const marketMapStore = useMarketMapStore();

//loading flags
const {
  saving,
  setSaving,
} = useLoadingFlags()

//composables
const {isXL} = useBreakpoint();
const toast = useToast();
const {games} = useConfigData('Currency')

//emits
const emit = defineEmits(['refresh']);

//props
const props = defineProps({});

//injects
const visible = inject('isShowAddMapModal')

//component variables
const marketMap = reactive({
  "game": null,
  "server": null,
  "link": null,
  "notifyChanges": true,
  "isActive": true
})
const rules = computed(() => {
  return {
    game: {required},
    server: {required},
    link: {required}
  };
});
const $vuelidate = useVuelidate(rules, marketMap);

const selectedGame = computed(() => games.value.find(g => g.game === marketMap.game) || null)
const servers = computed(() => selectedGame.value ? selectedGame.value.servers : [])

//global functions
//component functions
const onGameChange = () => {
  marketMap.server = servers.value.length === 1 ? servers.value[0] : ''
}
const onSave = async () => {
  try {
    saving.value = true
    const res = await marketMapStore.action_addWatchdog4(marketMap)
    if (res.result === true) {
      showMessage('New Map Added.', 'success');
      emit('refresh')
      setTimeout(() => {
        visible.value = false
      }, 1000)
    } else {
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    saving.value = false
  }
}

//hooks
watch(
    () => marketMap.game,
    () => onGameChange()
)

//provides
</script>
<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        Add Map
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm min-w-96 max-w-full">
      <div class="grid grid-cols-12 gap-4">
        <!-- ================== Game ================== -->
        <div class="col-span-6 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel class="w-full custom-select"
                      :class="{ 'has-error': $vuelidate.game.$error }"
                      variant="on">
            <Select v-model="marketMap.game" inputId="game" :options="games" optionLabel="game"
                    option-value="game"
                    class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                    :class="[$vuelidate.game.$error ? '!border !border-red-300' : '!border-slate-700']"
                    input-class="!text-xs 2xl:!text-sm"
                    filter autoFilterFocus/>
            <label for="game" class="!bg-slate-900">Game</label>
          </FloatLabel>
          <template v-if="$vuelidate.game.$error">
            <p class="text-red-600 dark:text-red-300 mt-1 text-xs"
               v-if="!$vuelidate.game.required.$response">
              This field is required
            </p>
          </template>
        </div>
        <div class="col-span-6 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel class="w-full custom-select"
                      :class="{ 'has-error': $vuelidate.server.$error }"
                      variant="on">
            <Select v-model="marketMap.server" inputId="server" :options="servers"
                    class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                    :class="[$vuelidate.server.$error ? '!border !border-red-300' : '!border-slate-700']"
                    input-class="!text-xs 2xl:!text-sm"
                    :virtualScrollerOptions="{ itemSize: 38 }"
                    filter autoFilterFocus/>
            <label for="server" class="!bg-slate-900">Server</label>
          </FloatLabel>
          <template v-if="$vuelidate.server.$error">
            <p class="text-red-300 mt-1 text-xs"
               v-if="!$vuelidate.server.required.$response">
              This field is required
            </p>
          </template>
        </div>
        <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="link" v-model="marketMap.link" autocomplete="off"
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                       :class="{ 'has-error': $vuelidate.link.$error }"/>
            <label for="link" class="!bg-slate-900"
                   :class="{ 'focus:text-red-300': $vuelidate.link.$error }">Link</label>
          </FloatLabel>
          <template v-if="$vuelidate.link.$error">
            <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
               v-if="!$vuelidate.link.required.$response">
              This field is required
            </p>
          </template>
        </div>
      </div>
      <div class="grid grid-cols-2">
        <div class="flex items-center gap-2 cursor-pointer w-max">
          <Checkbox v-model="marketMap.isActive" inputId="isActive" name="isActive" :value="true" binary/>
          <label for="isActive" class="cursor-pointer select-none"> Active </label>
        </div>
        <div class="flex items-center gap-2 cursor-pointer w-max">
          <Checkbox v-model="marketMap.notifyChanges" inputId="notifyChanges" name="notifyChanges" :value="true"
                    binary/>
          <label for="notifyChanges" class="cursor-pointer select-none"> Notify Changes </label>
        </div>
      </div>
    </div>

    <div class="flex justify-end gap-3 select-none">
      <button @click="onSave"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1">
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
        Save
      </button>
    </div>
  </Dialog>
</template>
<style></style>